import { useMemo, useState } from 'react';
import { estimateSpadesTricks, sortHand, spadesPartnerOf, spadesTeamOf, type SpadesLegal, type SpadesView } from '@cardhub/engine';
import { PlayingCard } from '../cards/PlayingCard';
import { Hand } from '../cards/Hand';
import { Modal } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { seatPosition, SeatView, MySeat, useNarrow, useSpeech } from './common';

const OFFSETS: Record<string, { x: number; y: number }> = {
  bottom: { x: 0, y: 34 },
  left: { x: -62, y: 0 },
  top: { x: 0, y: -34 },
  right: { x: 62, y: 0 },
};

export function SpadesTable({ scoresOpen, onCloseScores }: { scoresOpen: boolean; onCloseScores: () => void }) {
  const t = useT();
  const view = useGame((s) => s.view) as SpadesView;
  const legal = useGame((s) => s.legal) as SpadesLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const speech = useSpeech();
  const [selected, setSelected] = useState<string | null>(null);
  const hand = useMemo(() => sortHand(view.hand), [view.hand]);
  const playable = new Set(legal?.kind === 'play' ? legal.cards : []);
  const suggested = useMemo(() => Math.max(1, Math.min(13, Math.round(estimateSpadesTricks(view.hand) - 0.2))), [view.hand]);
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const teamName = (team: number) => `${name(team)} & ${name(team + 2)}`;
  const myTeam = mySeat !== null ? spadesTeamOf(mySeat) : 0;
  const myTurn = mySeat !== null && turn?.seats.includes(mySeat);
  const narrow = useNarrow();
  const trickSize = narrow ? 'md' : 'lg';
  const k = narrow ? 0.7 : 1;
  const bidLabel = (s: number) => (view.bids[s] === null ? null : view.bids[s] === 0 ? t('sp.nil') : String(view.bids[s]));
  const tricksTag = (s: number) => (view.bids[s] === null ? null : t('cb.tricks', { won: view.tricksWon[s] ?? 0, bid: bidLabel(s) ?? '-' }));
  const teamLine = (team: number) => `${view.teamScores[team] ?? 0} · ${t('sp.bags', { n: view.teamBags[team] ?? 0 })}`;

  const onCard = (c: string) => {
    if (!myTurn || legal?.kind !== 'play' || !playable.has(c)) return;
    setSelected(c);
    void act({ type: 'play', card: c }).finally(() => setSelected(null));
  };

  return (
    <>
      <div className="stage">
        <div className="felt" />
        {[0, 1, 2, 3].map((s) => (
          <SeatView
            key={s}
            seat={s}
            info={seats[s]}
            mySeat={mySeat}
            total={4}
            turn={turn}
            speech={speech[s]}
            sub={<span className="dim num">{view.teamScores[spadesTeamOf(s)] ?? 0} pts</span>}
            tags={
              <>
                {view.dealer === s && <span className="tag">D</span>}
                {mySeat !== null && s === spadesPartnerOf(mySeat) && <span className="tag">{t('sp.partner')}</span>}
                {tricksTag(s) && <span className="tag">{tricksTag(s)}</span>}
              </>
            }
          />
        ))}
        <div className="felt-center centered">
          <div className="center-note">
            {t('sp.round', { n: view.round })}
            {view.spadesBroken && ' · ♠ ' + t('sp.broken')}
            {view.phase === 'BIDDING' && !myTurn && turn?.seats[0] !== undefined && ` · ${t('table.waitingFor', { name: name(turn.seats[0]) })}`}
          </div>
          <div className="row" style={{ justifyContent: 'center', gap: 6 }} aria-label={t('table.scores')}>
            <span className="pill team-pill mine">{t('sp.us')}: {teamLine(myTeam)}</span>
            <span className="pill team-pill">{t('sp.them')}: {teamLine(1 - myTeam)}</span>
          </div>
          <div className={`trick ${view.phase === 'TRICK_END' ? 'collect' : ''}`} key={`trick-${view.round}-${view.tricks.length}-${view.phase === 'TRICK_END'}`} aria-label="Cards on the table">
            {view.trick.map((p, i) => {
              const dir = seatPosition(p.seat, mySeat, 4).dir;
              const o = OFFSETS[dir]!;
              const off = { x: o.x * k, y: o.y * k };
              const w = view.phase === 'TRICK_END' && view.lastTrick ? OFFSETS[seatPosition(view.lastTrick.winner, mySeat, 4).dir]! : null;
              const winnerDir = w ? { x: w.x * k, y: w.y * k } : null;
              return (
                <div
                  key={p.card}
                  className="slot fly"
                  style={{
                    transform: `translate(calc(-50% + ${off.x}px), calc(-50% + ${off.y}px)) rotate(${(i - 1.5) * 4}deg)`,
                    ['--from-x' as string]: `${off.x * 3}px`,
                    ['--from-y' as string]: `${off.y * 4}px`,
                    ...(winnerDir ? { ['--to-x' as string]: `${winnerDir.x * 3}px`, ['--to-y' as string]: `${winnerDir.y * 4}px`, animationDelay: '0.45s' } : {}),
                    zIndex: i,
                  }}
                >
                  <PlayingCard id={p.card} size={trickSize} />
                </div>
              );
            })}
          </div>
          {view.phase === 'ROUND_END' && view.roundResults.length > 0 && (
            <div className="card glass" style={{ textAlign: 'center' }}>
              <strong>{t('sp.roundScores', { n: view.round })}</strong>
              <div className="row" style={{ justifyContent: 'center', marginTop: 6 }}>
                {view.roundResults.at(-1)!.map((r, team) => (
                  <span key={team} className={`pill ${r.delta >= 0 ? 'accent' : 'danger'}`}>
                    {team === myTeam ? t('sp.us') : t('sp.them')}: {r.delta >= 0 ? '+' : ''}{r.delta}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        <MySeat
          seat={mySeat !== null ? seats[mySeat] : undefined}
          sub={mySeat !== null ? <>{view.teamScores[myTeam] ?? 0} pts{tricksTag(mySeat) ? ` · ${tricksTag(mySeat)}` : ''}</> : null}
        />
      </div>
      <div className="table-bottom">
        {myTurn && legal?.kind === 'bid' && <BidPicker min={legal.min} max={legal.max} suggested={suggested} onBid={(v) => void act({ type: 'bid', value: v })} />}
        <Hand size="lg" label="Your hand">
          {hand.map((c, i) => (
            <PlayingCard
              key={c}
              id={c}
              size="lg"
              dealIndex={view.phase === 'BIDDING' ? i : undefined}
              selected={selected === c}
              playable={myTurn && playable.has(c)}
              unplayable={myTurn && legal?.kind === 'play' && !playable.has(c)}
              onClick={() => onCard(c)}
            />
          ))}
        </Hand>
      </div>
      <Modal open={scoresOpen} onClose={onCloseScores} title={t('table.scores')}>
        <table className="scoreboard">
          <thead>
            <tr>
              <th>#</th>
              {[0, 1].map((team) => (
                <th key={team} className="ellipsis" style={{ maxWidth: 110 }}>{teamName(team)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.roundResults.map((row, r) => (
              <tr key={r}>
                <td>R{r + 1}</td>
                {row.map((x, team) => (
                  <td key={team} className="num" style={{ color: x.delta < 0 ? 'var(--danger)' : undefined }}>
                    {x.delta >= 0 ? '+' : ''}{x.delta}
                    <div className="dim" style={{ fontSize: 'var(--fs-xs)' }}>
                      {x.tricks}/{x.contract}
                      {x.nilPoints !== 0 ? ` · ${t('sp.nil')} ${x.nilPoints > 0 ? '✓' : '✗'}` : ''}
                      {x.bagPenalty > 0 ? ` · −${x.bagPenalty} ${t('sp.bagsWord')}` : ''}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td><strong>Total</strong></td>
              {view.teamScores.map((v, team) => (
                <td key={team} className="num"><strong>{v}</strong></td>
              ))}
            </tr>
            <tr>
              <td className="dim">{t('sp.bagsWord')}</td>
              {view.teamBags.map((v, team) => (
                <td key={team} className="num dim">{v}/{view.config.bagLimit}</td>
              ))}
            </tr>
          </tbody>
        </table>
        <p className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('sp.scoreHint', { target: view.config.targetScore, lose: view.config.loseScore })}</p>
      </Modal>
    </>
  );
}

/**
 * Bid picker: a Nil chip, then 1–8 ("+" reveals the rest), the suggested bid starred, and a separate confirm
 * button so a stray tap never commits a bid.
 */
function BidPicker({ min, max, suggested, onBid }: { min: number; max: number; suggested: number; onBid: (v: number) => void }) {
  const t = useT();
  const [value, setValue] = useState(suggested);
  const [more, setMore] = useState(suggested > 8);
  const [sent, setSent] = useState(false);
  const top = more ? max : Math.min(max, 8);
  const values = Array.from({ length: top - min + 1 }, (_, i) => min + i);
  return (
    <div className="bid-picker" role="group" aria-label={t('sp.bid')}>
      <div className="bid-head">
        <strong>{t('sp.yourBid')}</strong>
        <span className="dim">
          ★ {t('cb.suggested')}: <b>{suggested}</b>
        </span>
      </div>
      <div className={`bid-chips ${more ? 'more' : ''}`} role="radiogroup" aria-label={t('sp.bid')}>
        {values.map((v) => (
          <button
            key={v}
            role="radio"
            aria-checked={value === v}
            className={`bid-chip ${value === v ? 'on' : ''} ${v === suggested ? 'star' : ''}`}
            onClick={() => setValue(v)}
            aria-label={v === 0 ? t('sp.nilLong') : `${v}${v === suggested ? ` (${t('cb.suggested')})` : ''}`}
          >
            {v === 0 ? t('sp.nil') : v}
          </button>
        ))}
        {!more && max > 8 && (
          <button className="bid-chip plus" onClick={() => setMore(true)} aria-label={t('cb.moreBids')}>
            +
          </button>
        )}
      </div>
      <button
        className="btn primary block bid-confirm"
        disabled={sent}
        onClick={() => {
          setSent(true);
          onBid(value);
          setTimeout(() => setSent(false), 1500);
        }}
      >
        {value === 0 ? t('sp.bidNil') : t('sp.bidValue', { n: value })}
      </button>
    </div>
  );
}
